import { DunResult, ParliamentResult, ElectionDataset, dunByParliament } from "@/lib/data";
import { party, partyLabel } from "@/lib/parties";
import { PartyBadge, ShareBars } from "./ElectionBits";
import { X, MapPin, CheckCircle2, Radio } from "lucide-react";
import { useLive } from "@/contexts/LiveDataContext";

/**
 * 2026 live-result block. Leads the seat panel in Live mode.
 * Shows status (Declared/Leading), the 2026 winner/leader, optional full
 * 2026 candidate breakdown and turnout. Driven entirely by live.ts.
 */
function Live2026Block({ code }: { code: string }) {
  const LIVE = useLive();
  const s = LIVE.seatStatus[code];
  const lineup = LIVE.candidatesByContest[code] ?? [];
  const hasVotes = lineup.some((c) => typeof c.votes === "number" && c.votes > 0);
  const p = s ? party(s.party) : null;
  const isPending = !s || s.status === "pending";
  const label = !s ? "Awaiting result" : s.status === "won_official" ? "Final" : (s.status === "won" || s.status === "won_unofficial" || s.status === "leading") ? "In count" : "Awaiting result";


  return (
    <section className="border-2 border-foreground bg-card">
      <div className="flex items-center justify-between border-b border-border bg-foreground px-2.5 py-1">
        <span className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wider text-background">
          <Radio className="h-3 w-3" /> 2026 · live
        </span>
        <span
          className="flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-bold uppercase"
          style={{
            color: isPending ? undefined : "#fff",
            background: isPending ? "transparent" : p!.color,
          }}
        >
          {s?.status === "leading" && (
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
            </span>
          )}
          {(s?.status === "won" || s?.status === "won_unofficial" || s?.status === "won_official") && (
            <CheckCircle2 className="h-3.5 w-3.5 text-white" />
          )}
          <span className={isPending ? "text-muted-foreground" : ""}>{label}</span>
        </span>
      </div>

      <div className="p-3">
        {lineup.length > 0 ? (
          <>
            {hasVotes ? (
              <>
                <ShareBars
                  rows={(() => {
                    const total = lineup.reduce((a, c) => a + (c.votes ?? 0), 0) || 1;
                    return lineup
                      .slice()
                      .sort((a, b) => (b.votes ?? 0) - (a.votes ?? 0))
                      .map((c) => ({
                        party: c.party,
                        sub: c.sub,
                        name: c.name,
                        pct: ((c.votes ?? 0) / total) * 100,
                        votes: c.votes ?? 0,
                      }));
                  })()}
                />
                {(() => {
                  const sorted = lineup.slice().sort((a, b) => (b.votes ?? 0) - (a.votes ?? 0));
                  const totalValid = sorted.reduce((a, c) => a + (c.votes ?? 0), 0);
                  const majority = sorted.length >= 2 ? (sorted[0].votes ?? 0) - (sorted[1].votes ?? 0) : 0;
                  return (
                    <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                      {majority > 0 && <span className="tnum">Majority: {majority.toLocaleString()}</span>}
                      {majority > 0 && totalValid > 0 && <span>·</span>}
                      {totalValid > 0 && <span className="tnum">{totalValid.toLocaleString()} valid votes</span>}
                    </div>
                  );
                })()}
              </>
            ) : (
              <div className="space-y-2">
                {lineup.map((c, i) => (
                  <div key={i}>
                    <div className="flex items-baseline justify-between gap-2 text-xs">
                      <span className="truncate font-medium">
                        {c.name}{" "}
                        <span className="text-muted-foreground">
                          ({partyLabel(c.party, c.sub)})
                        </span>
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="h-2.5 flex-1 overflow-hidden rounded-sm bg-muted">
                        <div
                          className="h-full rounded-sm"
                          style={{
                            width: "0%",
                            background: party(c.party).color,
                          }}
                        />
                      </div>
                      <span className="tnum w-16 shrink-0 text-right text-xs text-muted-foreground">
                        —
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : null}
      </div>
    </section>
  );
}

interface Props {
  ds: ElectionDataset;
  level: "state" | "parliament";
  code: string | null;
  onClose: () => void;
  onSelectDun: (code: string) => void;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-sm border border-border bg-secondary/40 px-2.5 py-1.5">
      <div className="kicker text-muted-foreground text-[9px]">{label}</div>
      <div className="tnum mt-0.5 text-sm font-bold leading-none">{value}</div>
    </div>
  );
}

export default function SeatPanel({ ds, level, code, onClose, onSelectDun }: Props) {
  const LIVE = useLive();
  if (!code) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-5 text-center text-muted-foreground">
        <MapPin className="mb-2 h-6 w-6 opacity-40" />
        <p className="font-serif-ed text-base">Select a constituency</p>
        <p className="mt-1 max-w-xs text-xs">
          Click any seat on the map, or search above, to see the full result, every
          candidate, and the voter profile.
        </p>
      </div>
    );
  }

  if (level === "state") {
    const seat = ds.dun.find((d) => d.code === code);
    if (!seat) return null;
    const p = party(seat.party);
    const parent = ds.parliament.find((x) => x.code === seat.parliament);
    const turnout = seat.registered ? (seat.valid / seat.registered) * 100 : 0;

    return (
      <div className="flex h-full flex-col">
        <Header
          kicker={`State seat · ${seat.code}${parent ? ` · ${parent.code} ${parent.name}` : ""}`}
          title={seat.name}
          onClose={onClose}
        />
        <div className="flex-1 space-y-3 overflow-y-auto px-3 pb-4 pt-2">
          {LIVE.isLive && LIVE.level === "state" && <Live2026Block code={seat.code} />}
          <section>
            <div className="flex items-center gap-1.5 mb-1">
              <CheckCircle2 className="h-3 w-3" style={{ color: p.color }} />
              <span className="text-[10px] font-bold uppercase tracking-wide text-foreground/70">
                2022 Result
              </span>
              <span className="ml-auto tnum text-[10px] text-muted-foreground">
                {seat.registered.toLocaleString()} registered · {turnout.toFixed(1)}% turnout
              </span>
            </div>
            <ShareBars
              rows={seat.candidates.map((c) => ({
                party: c.party,
                sub: c.sub,
                name: c.name,
                pct: c.pct,
                votes: c.votes,
              }))}
            />
            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <span className="tnum">Majority: {seat.majority.toLocaleString()}</span>
              <span>·</span>
              <span className="tnum">{seat.valid.toLocaleString()} valid votes</span>
            </div>
          </section>


        </div>
      </div>
    );
  }

  // Parliament level
  const seat = ds.parliament.find((d) => d.code === code) as ParliamentResult;
  if (!seat) return null;
  const p = party(seat.party);
  const children = dunByParliament(ds, seat.code);

  return (
    <div className="flex h-full flex-col">
      <Header kicker={`Parliamentary seat · ${seat.code}`} title={seat.name} onClose={onClose} />
      <div className="flex-1 space-y-3 overflow-y-auto px-3 pb-4 pt-2">
        <div
          className="rounded-md border-l-4 bg-card p-3 shadow-sm"
          style={{ borderColor: p.color }}
        >
          <div className="flex items-center gap-1.5">
            <CheckCircle2 className="h-3 w-3" style={{ color: p.color }} />
            <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">MP · GE15 (2022)</span>
          </div>
          <div className="mt-0.5 font-serif-ed text-base font-semibold">{seat.winner}</div>
          <div className="mt-1.5 flex items-center gap-2">
            <PartyBadge code={seat.party} sub={seat.sub} />
            <span className="tnum text-xs text-muted-foreground">
              maj. {seat.majority.toLocaleString()} ({seat.majority_pct.toFixed(1)}%)
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-1.5">
          <Stat label="Registered" value={seat.registered.toLocaleString()} />
          <Stat label="Votes cast" value={seat.votes_cast.toLocaleString()} />
          <Stat label="Turnout" value={`${seat.turnout.toFixed(1)}%`} />
          <Stat label="Rejected" value={`${seat.rejected_pct.toFixed(1)}%`} />
        </div>

        <section>
          <SectionTitle>State seats within ({children.length})</SectionTitle>
          <div className="space-y-1">
            {children.map((c) => (
              <button
                key={c.code}
                onClick={() => onSelectDun(c.code)}
                className="flex w-full items-center justify-between rounded-sm border border-border bg-card px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-secondary"
              >
                <span className="flex items-center gap-1.5">
                  <span
                    className="inline-block h-2 w-2 rounded-sm"
                    style={{ background: party(c.party).color }}
                  />
                  <span className="font-medium">
                    {c.code} {c.name}
                  </span>
                </span>
                <span className="text-[11px] text-muted-foreground">{c.winner}</span>
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function Header({
  kicker,
  title,
  onClose,
}: {
  kicker: string;
  title: string;
  onClose: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-2 border-b border-border px-4 py-2.5">
      <div>
        <div className="kicker text-primary text-[10px]">{kicker}</div>
        <h3 className="mt-0.5 font-serif-ed text-lg font-bold leading-tight">{title}</h3>
      </div>
      <button
        onClick={onClose}
        className="rounded-sm p-1 text-muted-foreground transition-colors hover:bg-secondary"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="mb-2.5 border-b border-border pb-1.5 text-sm font-bold uppercase tracking-wide text-foreground/70">
      {children}
    </h4>
  );
}
